import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, filter, fromEvent, from, timer } from 'rxjs';
import { switchMap, tap, throttleTime } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { environment } from '../../../environments/environment';
import { APP_VERSION } from '../constants/app-version';
import {
  AppVersionInfo,
  VersionCheckConfig,
} from '../interfaces/app-version.interface';

/**
 * Soft-update detection.
 *
 * Running fingerprint = APP_VERSION baked into the JS bundle at build time.
 * Remote fingerprint = `/version.json` on the server (fetched with cache: 'no-store').
 * When they differ, prompt the user to reload.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private readonly versionUrl = '/version.json';
  private readonly config: VersionCheckConfig = environment.versionCheck;

  /** Version compiled into this tab's JS bundle. */
  private readonly runningVersion: AppVersionInfo = { ...APP_VERSION };
  private started = false;
  private promptOpen = false;
  private notifiedVersion: string | null = null;
  private subscriptions = new Subscription();
  private destroyHookRegistered = false;

  start(): void {
    if (this.started || !this.config.enabled) {
      return;
    }
    this.started = true;

    if (!this.destroyHookRegistered) {
      this.destroyHookRegistered = true;
      this.destroyRef.onDestroy(() => this.stop());
    }

    this.log('Started', {
      running: this.runningVersion,
      pollIntervalMs: this.config.pollIntervalMs,
    });

    // Immediate check on open (covers close-tab / reopen with a stale cached shell).
    this.checkForUpdate();

    const intervalMs = Math.max(this.config.pollIntervalMs, 10_000);
    this.subscriptions.add(
      timer(intervalMs, intervalMs)
        .pipe(
          filter(() => !this.promptOpen),
          switchMap(() => from(this.fetchRemoteVersion())),
          tap((remote) => {
            this.ngZone.run(() => this.handleRemoteVersion(remote));
          }),
        )
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(document, 'visibilitychange')
        .pipe(
          filter(() => document.visibilityState === 'visible'),
          tap(() => this.checkForUpdate()),
        )
        .subscribe(),
    );

    // Check again when the user navigates within the SPA (throttled so rapid
    // route changes don't trigger a burst of requests).
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          throttleTime(10_000),
          tap(() => this.checkForUpdate()),
        )
        .subscribe(),
    );
  }

  stop(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.started = false;
    this.promptOpen = false;
  }

  checkForUpdate(): void {
    if (!this.config.enabled || this.promptOpen) {
      return;
    }
    void this.fetchRemoteVersion().then((remote) => {
      this.ngZone.run(() => this.handleRemoteVersion(remote));
    });
  }

  /**
   * A deploy is detected purely from the `version` fingerprint (build number + git sha),
   * which is unique per build. `buildTime` is intentionally NOT compared: the baked-in
   * constant and the deployed `version.json` are written at slightly different moments in
   * CI, so comparing it produced a false "new version" on every reload even though the
   * running bundle and the server agreed on `version`. This also keeps the check consistent
   * with the early-boot script in `index.html`, which compares `version` only.
   */
  isNewerVersion(current: AppVersionInfo, remote: AppVersionInfo): boolean {
    if (!current?.version || !remote?.version) {
      return false;
    }
    return current.version !== remote.version;
  }

  private async fetchRemoteVersion(): Promise<AppVersionInfo | null> {
    try {
      const response = await fetch(`${this.versionUrl}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (!response.ok) {
        this.log(`HTTP ${response.status} for ${this.versionUrl}`);
        return null;
      }
      const body: unknown = await response.json();
      return this.normalizeVersion(body);
    } catch (err) {
      this.log('Fetch failed', err);
      return null;
    }
  }

  private normalizeVersion(body: unknown): AppVersionInfo | null {
    if (!body || typeof body !== 'object') {
      return null;
    }
    const version = (body as { version?: unknown }).version;
    const buildTime = (body as { buildTime?: unknown }).buildTime;
    if (typeof version !== 'string' || !version.trim()) {
      return null;
    }
    return {
      version: version.trim(),
      buildTime: typeof buildTime === 'string' ? buildTime : '',
    };
  }

  private handleRemoteVersion(remote: AppVersionInfo | null): void {
    if (!remote) {
      return;
    }

    const changed = this.isNewerVersion(this.runningVersion, remote);
    this.log('Compare', { running: this.runningVersion, remote, changed });

    if (!changed || this.notifiedVersion === remote.version || this.promptOpen) {
      return;
    }
    this.promptUserToRefresh(remote);
  }

  private promptUserToRefresh(remote: AppVersionInfo): void {
    this.promptOpen = true;
    this.notifiedVersion = remote.version;
    this.log('Showing update dialog', remote);

    const hasUnsavedChanges = this.hasUnsavedChanges();
    const message = this.t(
      'newVersionAvailable',
      'Reload to continue using the latest version and improvements.',
    );
    const unsavedWarning = this.t(
      'newVersionUnsavedWarning',
      'Unsaved changes will be lost if you reload before saving.',
    );
    const html = hasUnsavedChanges
      ? `<p>${message}</p><p style="margin-top:0.75rem;font-weight:600;">${unsavedWarning}</p>`
      : `<p>${message}</p>`;

    void Swal.fire({
      icon: hasUnsavedChanges ? 'warning' : 'info',
      title: this.t(
        'newVersionAvailableTitle',
        'A new version of the application is available.',
      ),
      html,
      showCancelButton: true,
      confirmButtonText: this.t('reloadNow', 'Reload Now'),
      cancelButtonText: this.t('later', 'Later'),
      allowOutsideClick: false,
      allowEscapeKey: true,
      reverseButtons: true,
      heightAuto: false,
    }).then((result) => {
      this.promptOpen = false;
      if (result.isConfirmed) {
        this.reloadApplication();
      }
    });
  }

  /**
   * Best-effort detection of in-progress edits so we can warn before reloading.
   * Angular marks changed reactive/template-driven controls with `.ng-dirty`.
   */
  private hasUnsavedChanges(): boolean {
    try {
      return !!document.querySelector('.ng-dirty');
    } catch {
      return false;
    }
  }

  /**
   * Reload to pick up the freshly deployed shell/bundles. Uses `location.replace`
   * (not a back-navigable push) and strips any legacy `_v` cache-busting param so the
   * address bar stays clean — no version is ever written to the URL.
   */
  reloadApplication(): void {
    window.location.replace(this.buildReloadUrl(window.location.href));
  }

  /** Reload target URL with any legacy `_v` cache-busting param removed. */
  buildReloadUrl(currentHref: string): string {
    const url = new URL(currentHref);
    url.searchParams.delete('_v');
    return url.toString();
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? fallback : value;
  }

  private log(message: string, data?: unknown): void {
    if (environment.production) {
      return;
    }
    if (data !== undefined) {
      console.log(`[VersionCheck] ${message}`, data);
    } else {
      console.log(`[VersionCheck] ${message}`);
    }
  }
}
